import { debounce } from './theme.js';

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
    this.addEventListener('change', this.uncheckCategoryChildren);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler = (event) => {
    const trigger = event.target.closest('[data-render-section-url]');
    if (!trigger || trigger.hasAttribute('disabled') || trigger.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    const query = trigger.dataset.renderSectionUrl.split('?')[1] || '';
    const searchParams = new URLSearchParams(query).toString();
    this.fetchSection(searchParams);
  };

  /**
   * Unticking a category also unticks its subcategories and types, so no hidden drill-down filter stays applied.
   * Values hold full paths like "Tops > Outerwear".
   * @param {Event} event
   */
  uncheckCategoryChildren = (event) => {
    const input = event.target;
    if (input.type !== 'checkbox' || input.checked || !input.form) return;

    const childPrefix = `${input.value} > `;
    for (const child of input.form.querySelectorAll('input[type="checkbox"]:checked')) {
      if (child.value.startsWith(childPrefix)) child.checked = false;
    }
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const existingParams = new URLSearchParams(window.location.search);
    const qValue = existingParams.get('q');

    this.removeDefaultPriceFilters(searchParams, form);

    const finalParams = qValue ? `q=${encodeURIComponent(qValue)}&${searchParams}` : searchParams.toString();
    this.fetchSection(finalParams);
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  /**
   * The sticky bar's In Stock box sits outside the re-rendered grid, so refresh it from the response too.
   * @param {Document} html - Parsed section response.
   * @returns {void}
   */
  updateStickyFilterStock = (html) => {
    const source = html.querySelector('[data-sticky-filter-stock]');
    const destination = this.querySelector('[data-sticky-filter-stock]');
    if (source && destination) destination.innerHTML = source.innerHTML;
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      const destination = document.getElementById(elementToRender.id);
      destination.innerHTML = elementToRender.innerHTML;
      // Drill-down filters render hidden until their parent is selected
      destination.hidden = elementToRender.hidden;
    });
  };

  /* `?.` because four of these five only exist when filtering/sorting is on. The search
     template has both off, so the bare lookups threw here and killed the fetch before it
     started — which broke pagination, since page links route through fetchSection. */
  showLoadingOverlay = () => {
    this.querySelector(`#loading-overlay-${this.dataset.section}`)?.style.setProperty('display', 'flex');
    this.querySelector(`#loading-spinner-${this.dataset.section}`)?.style.setProperty('display', 'block');
    this.querySelector(`#results-count-${this.dataset.section}`)?.style.setProperty('display', 'none');
    this.querySelector(`#drawer-results-count-${this.dataset.section}`)?.style.setProperty('display', 'none');
    this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`)?.style.setProperty('display', 'block');
  };

  hideLoadingOverlay = () => {
    this.querySelector(`#loading-overlay-${this.dataset.section}`)?.style.setProperty('display', 'none');
    this.querySelector(`#loading-spinner-${this.dataset.section}`)?.style.setProperty('display', 'none');
    this.querySelector(`#results-count-${this.dataset.section}`)?.style.setProperty('display', 'block');
    this.querySelector(`#drawer-results-count-${this.dataset.section}`)?.style.setProperty('display', 'block');
    this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`)?.style.setProperty('display', 'none');
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateSourceFromDestination(html, `filters-sidebar-actions-${this.dataset.section}`);
        this.updateStickyFilterStock(html);
        this.updateFilters(html, `js-filter`);
        this.hideLoadingOverlay();
        this.scrollToProductGrid();
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };

  scrollToProductGrid = () => {
    const productGrid = this.querySelector(`#product-grid-${this.dataset.section}`);
    const headerId = document.querySelector('#main-header');
    const headerOffset = (headerId.offsetHeight + 10) || 80;

    if (productGrid) {
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}